import { cn } from '@invent/wl-ui-kit-next';
import { Button } from '@invent/wl-ui-kit-next/ui';
import { useId } from 'react';

import { StatusChip } from '@/design';

import type { IAnswer, IFinding, ISnapshotUsage } from '../api/contracts';
import type { UiValue } from './answer-values';
import { PERSON_REGISTRIES, prefillLock, priorValueText, sourceCopy } from './prefill-view';
import { addressKey, domAddress, findingText } from './snapshot-view';
import type { IAnswerAddress } from './use-response';

/**
 * A main office address a registry proposed, drawn as one address to confirm or refuse (the
 * survey review's FA5, the plan's task 5.5): five proposals read as one fact, so they are decided
 * as one — "Confirm address" or "Not my address", one confirmation batch either way.
 *
 * Only while the address is wholly a registry's open proposal: every line holding a proposal holds
 * one from the same registry, undecided, unlocked and not being typed over. A line the registry
 * left empty (no proposal behind it) stays a field of its own after the block. Once any line is
 * decided — confirmed, typed over, refused — the address is the respondent's again and draws as
 * fields, each with its own prefill note, as every other proposal does. The country (FA-AP-016) is
 * not a line: no registry spells the list's code, and it is never guessed from a ZIP (D17).
 *
 * The rejected alternative is a block that stays after a decision, which would hide five fields
 * behind a summary the respondent can no longer act on.
 */

/** A survey's main office lines by usage code — this app's words for a layout survey-api sends
 *  no hint for (D14): FA's alone, the address FA5 is about. */
interface IOfficeLines {
  street: string;
  suite: string;
  city: string;
  state: string;
  zip: string;
}

const OFFICE_ADDRESSES: readonly IOfficeLines[] = [
  { street: 'FA-AP-011', suite: 'FA-AP-012', city: 'FA-AP-013', state: 'FA-AP-014', zip: 'FA-AP-015' }
];

/** One proposed value: its usage, the value as it reads, and what parts it from the one before. */
interface IAddressPart {
  code: string;
  text: string;
  before: string;
}

export interface IProposedAddress {
  /** The proposed lines' usage codes, in the address's order. */
  codes: string[];
  /** The registry that proposed them (`provenance.source`). */
  source: string;
  /** The address as it reads: the street, the suite, then the city, state and ZIP on one line. */
  lines: IAddressPart[][];
}

/**
 * The office addresses of a section a registry proposed whole (see the module's doc), each by
 * the usages the section draws. `usageOf` answers undefined for a usage the section does not draw
 * or hides, so a hidden line keeps its address out of the block.
 */
export function proposedAddresses(
  answers: Map<string, IAnswer>,
  drafts: Map<string, UiValue>,
  usageOf: (code: string) => ISnapshotUsage | undefined
): IProposedAddress[] {
  return OFFICE_ADDRESSES.map((lines) => proposedAddress(lines, answers, drafts, usageOf)).filter(
    (address): address is IProposedAddress => address !== null
  );
}

function proposedAddress(
  lines: IOfficeLines,
  answers: Map<string, IAnswer>,
  drafts: Map<string, UiValue>,
  usageOf: (code: string) => ISnapshotUsage | undefined
): Nullable<IProposedAddress> {
  const parts = new Map<string, IAddressPart>();
  let source: Nullable<string> = null;
  for (const code of Object.values(lines)) {
    const key = addressKey(code, null, '');
    const answer = answers.get(key);
    const provenance = answer?.provenance;
    if (!provenance) continue;
    const usage = usageOf(code);
    if (
      !usage ||
      provenance.state !== 'proposed' ||
      !PERSON_REGISTRIES.has(provenance.source) ||
      (source !== null && provenance.source !== source) ||
      prefillLock(usage, answer) !== null ||
      drafts.has(key)
    ) {
      return null;
    }
    source = provenance.source;
    const text = priorValueText(provenance.sourceValue, usage);
    if (text) parts.set(code, { code, text, before: '' });
  }
  if (source === null || parts.size === 0) return null;

  // "AUSTIN, Texas 78701": a comma after the city, a space between the state and its ZIP.
  const line = (...codes: string[]) =>
    codes
      .map((code) => parts.get(code))
      .filter((part): part is IAddressPart => !!part)
      .map((part, index, drawn) => {
        let before = ', ';
        if (index === 0) before = '';
        else if (part.code === lines.zip && drawn[index - 1].code === lines.state) before = ' ';
        return { ...part, before };
      });
  return {
    codes: [...parts.keys()],
    source,
    lines: [line(lines.street), line(lines.suite), line(lines.city, lines.state, lines.zip)].filter(
      (parts) => parts.length > 0
    )
  };
}

/**
 * The block: the address, the findings its lines carry (said once each), and the two decisions.
 * Each value carries its usage's `data-address`, so the form's hooks and "Go to question" still
 * find every line.
 */
export function OfficeAddressBlock({
  address,
  findings,
  usageOf,
  onDecide
}: {
  address: IProposedAddress;
  findings: Map<string, IFinding[]>;
  usageOf: (code: string) => ISnapshotUsage | undefined;
  onDecide: (addresses: IAnswerAddress[], action: 'confirm' | 'reject') => void;
}) {
  const decide = (action: 'confirm' | 'reject') =>
    onDecide(
      address.codes.map((usage) => ({ usage, instanceId: null, instanceKey: '' })),
      action
    );
  const hard = new Set(
    address.codes
      .flatMap((code) => findings.get(addressKey(code, null, '')) ?? [])
      .filter((finding) => finding.severity === 'hard')
      .map((finding) => findingText(finding, usageOf, 'inline'))
  );
  const copy = sourceCopy(address.source);
  const titleId = useId();

  return (
    <div
      role="group"
      aria-labelledby={titleId}
      // The left rule and tint a field takes for a hard finding (`ResponseField`).
      className={cn(
        'grid gap-2 rounded-r-md border-l-2 border-transparent px-3 py-2 transition-colors',
        hard.size > 0 && 'border-destructive bg-destructive/5'
      )}
      data-office-address
    >
      <p id={titleId} className="leading-snug font-medium">
        Main office address
      </p>
      <address className="not-italic">
        {address.lines.map((parts) => (
          <span key={parts[0].code} className="block">
            {parts.map((part) => (
              <span key={part.code}>
                {part.before}
                <span data-address={domAddress(part.code, null, '')}>{part.text}</span>
              </span>
            ))}
          </span>
        ))}
      </address>
      {[...hard].map((words) => (
        <p key={words} className="text-sm text-destructive">
          {words}
        </p>
      ))}
      <div className="mt-1 flex flex-wrap items-center gap-2" data-prefill="proposed">
        <StatusChip tone="info">{`${copy.proposed} — please confirm`}</StatusChip>
        <Button type="button" size="sm" variant="outline" onClick={() => decide('confirm')}>
          Confirm address
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => decide('reject')}>
          Not my address
        </Button>
      </div>
    </div>
  );
}
